import { findGroup, locateRow } from "../find";
import { clampSectionInsertIndex } from "../row-policy";
import type { EditorDraft, RowParent } from "./types";
import { applySectionSelection } from "./shared";

export function moveTopLevelSection(
  draft: EditorDraft,
  fromIndex: number,
  toIndex: number,
): boolean {
  if (fromIndex === toIndex || fromIndex === toIndex - 1) {
    return false;
  }
  const sections = draft.document.sections;
  if (fromIndex < 0 || fromIndex >= sections.length) {
    return false;
  }
  const [moved] = sections.splice(fromIndex, 1);
  if (!moved) {
    return false;
  }
  let insertAt = clampSectionInsertIndex(sections, toIndex);
  if (fromIndex < insertAt) {
    insertAt -= 1;
  }
  sections.splice(insertAt, 0, moved);
  applySectionSelection(draft, moved);
  return true;
}

export function moveRow(
  draft: EditorDraft,
  fromIndex: number,
  toIndex: number,
): boolean {
  return moveTopLevelSection(draft, fromIndex, toIndex);
}

export function moveRowInGroup(
  draft: EditorDraft,
  groupId: string,
  fromIndex: number,
  toIndex: number,
): boolean {
  if (fromIndex === toIndex || fromIndex === toIndex - 1) {
    return false;
  }
  const group = findGroup(draft.document.sections, groupId);
  if (!group) {
    return false;
  }
  if (fromIndex < 0 || fromIndex >= group.rows.length) {
    return false;
  }
  const [moved] = group.rows.splice(fromIndex, 1);
  if (!moved) {
    return false;
  }
  let insertAt = toIndex;
  if (fromIndex < insertAt) {
    insertAt -= 1;
  }
  insertAt = Math.max(0, Math.min(insertAt, group.rows.length));
  group.rows.splice(insertAt, 0, moved);
  draft.selection = { kind: 'row', rowId: moved.id };
  return true;
}

export function moveRowBetweenParents(
  draft: EditorDraft,
  rowId: string,
  to: RowParent,
): boolean {
  const located = locateRow(draft.document.sections, rowId);
  if (!located) {
    return false;
  }
  if (
    located.location.parent === 'document' &&
    draft.document.sections.length <= 1 &&
    to.parent !== 'document'
  ) {
    return false;
  }

  const row = located.row;

  if (located.location.parent === 'document') {
    draft.document.sections.splice(located.location.sectionIndex, 1);
  } else {
    const sourceGroup = findGroup(
      draft.document.sections,
      located.location.groupId,
    );
    if (!sourceGroup) {
      return false;
    }
    sourceGroup.rows.splice(located.location.rowIndex, 1);
  }

  if (to.parent === 'document') {
    const insertAt = clampSectionInsertIndex(draft.document.sections, to.index);
    draft.document.sections.splice(insertAt, 0, row);
  } else {
    const destGroup = findGroup(draft.document.sections, to.groupId);
    if (!destGroup) {
      return false;
    }
    const insertAt = Math.max(0, Math.min(to.rowIndex, destGroup.rows.length));
    destGroup.rows.splice(insertAt, 0, row);
  }

  draft.selection = { kind: 'row', rowId: row.id };
  return true;
}
